import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth-context';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  Palette,
} from 'lucide-react';
import { SegmentEditModal } from '../../components/segments/SegmentEditModal';

interface Segment {
  id: string;
  segment_name: string;
  segment_slug: string;
  description: string;
  color: string;
  characteristics: any;
  marketing_guidelines: any;
  user_count: number;
  is_active: boolean;
}

export default function SegmentManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedSite } = useSite();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState<Segment | null>(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (selectedSite) {
      fetchSegments();
    }
  }, [selectedSite]);

  const fetchSegments = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(`/segment-management/${siteId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch segments');
      }
      
      const data = await response.json();
      setSegments(data.segments || []);
    } catch (error) {
      console.error('Error fetching segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (segment: Segment) => {
    setSelectedSegment(segment);
    setEditModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedSegment(null);
    setEditModalOpen(true);
  };

  const handleDelete = (segment: Segment) => {
    setSegmentToDelete(segment);
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!segmentToDelete || !selectedSite) return;

    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(
        `/segment-management/${siteId}/${segmentToDelete.id}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const error = await response.json();
        if (error.userCount) {
          setDeleteError(
            `Cannot delete: ${error.userCount} users are assigned to this segment`
          );
          return;
        }
        throw new Error(error.error || 'Failed to delete segment');
      }

      setDeleteDialogOpen(false);
      setSegmentToDelete(null);
      fetchSegments();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete segment');
    }
  };

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select a site to manage segments</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Segment Management</h1>
          <p className="text-muted-foreground mt-2">
            Create, edit, and manage customer segments for {selectedSite.domain}
          </p>
        </div>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Segment
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Customer Segments</CardTitle>
          <CardDescription>
            Define and customize how you categorize your visitors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-muted-foreground">Loading segments...</div>
            </div>
          ) : segments.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No segments defined yet</p>
              <Button onClick={handleCreate}>Create Your First Segment</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Color</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Users</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {segments.map((segment) => (
                  <TableRow key={segment.id}>
                    <TableCell>
                      <div
                        className="w-6 h-6 rounded-full border border-gray-300"
                        style={{ backgroundColor: segment.color || '#6B7280' }}
                        title={segment.color}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {segment.segment_name}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <div className="space-y-1">
                        <span className="text-sm text-muted-foreground">
                          {segment.description || 'No description'}
                        </span>
                        {segment.characteristics?.criteria && segment.characteristics.criteria.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {segment.characteristics.criteria.slice(0, 3).map((c: any, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {c.metric} {c.operator} {c.value}{c.unit}
                              </Badge>
                            ))}
                            {segment.characteristics.criteria.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{segment.characteristics.criteria.length - 3} more
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {segment.user_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {segment.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(segment)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(segment)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SegmentEditModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedSegment(null);
        }}
        segment={selectedSegment}
        siteId={selectedSite?._id || selectedSite?.id || ''}
        onSave={fetchSegments}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Segment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the segment "{segmentToDelete?.segment_name}"?
              {segmentToDelete?.user_count && segmentToDelete.user_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  Warning: This segment has {segmentToDelete.user_count} users assigned.
                </span>
              )}
            </AlertDialogDescription>
            {deleteError && (
              <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                <AlertCircle className="h-4 w-4" />
                {deleteError}
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}